/**
 * Smooth count-up for live metric values (Stage L).
 *
 * Rules:
 *  - The first render shows the authoritative value immediately (no 0 → 24
 *    animation on mount or remount).
 *  - Later changes animate from the currently displayed value, so a rapid
 *    update mid-animation continues toward the newest target instead of
 *    restarting from zero.
 *  - A decrease (correction / dedupe) animates downward.
 *  - `prefers-reduced-motion` users, and environments without rAF (tests, SSR),
 *    get the value immediately.
 */
import { useEffect, useRef, useState } from "react";

export const DEFAULT_COUNT_UP_MS = 420;

export function prefersReducedMotion() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return true;
  try {
    return Boolean(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  } catch {
    return true;
  }
}

function animateSupported() {
  return (
    typeof window !== "undefined"
    && typeof window.requestAnimationFrame === "function"
    && !prefersReducedMotion()
  );
}

/**
 * @param {number} value authoritative target
 * @param {{duration?: number}} options
 * @returns {number} value to display this frame
 */
export function useAnimatedNumber(value, { duration = DEFAULT_COUNT_UP_MS } = {}) {
  const target = Number.isFinite(Number(value)) ? Number(value) : 0;
  const [display, setDisplay] = useState(target);
  const displayRef = useRef(target);
  const frameRef = useRef(null);
  const mountedRef = useRef(false);

  useEffect(() => {
    if (!mountedRef.current) {
      // Mount / remount: no animation from zero.
      mountedRef.current = true;
      displayRef.current = target;
      setDisplay(target);
      return undefined;
    }
    const from = displayRef.current;
    if (from === target) return undefined;
    if (!animateSupported()) {
      displayRef.current = target;
      setDisplay(target);
      return undefined;
    }
    const startedAt = typeof performance !== "undefined" ? performance.now() : Date.now();
    const tick = (now) => {
      const elapsed = Math.max(0, now - startedAt);
      const t = Math.min(1, elapsed / Math.max(1, duration));
      const eased = 1 - Math.pow(1 - t, 3);
      const next = from + (target - from) * eased;
      if (t >= 1) {
        displayRef.current = target;
        setDisplay(target);
        frameRef.current = null;
        return;
      }
      displayRef.current = next;
      setDisplay(next);
      frameRef.current = window.requestAnimationFrame(tick);
    };
    frameRef.current = window.requestAnimationFrame(tick);
    return () => {
      if (frameRef.current != null) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [target, duration]);

  return display;
}
